import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Cell } from 'recharts';
import { api } from '../../api.js';
import { Card, CardHeader, ScoreBadge, PageHeader, Spinner, EmptyState, Badge } from '../../components/ui.jsx';
import { Users } from 'lucide-react';

export default function GroupInsightsPage() {
  const { id } = useParams();
  const { data, isLoading } = useQuery({ queryKey: ['team-insights', id], queryFn: () => api.teamInsights(id) });
  if (isLoading || !data) return <div className="min-h-[40vh] grid place-items-center"><Spinner className="w-8 h-8 text-primary-600" /></div>;

  const matrix = data.capability_matrix || [];
  const dist = data.distribution_by_competency || {};
  const competencyKeys = Object.keys(dist);

  return (
    <>
      <PageHeader
        title={data.group?.name || 'Team'}
        description={`${data.members_count} members · ${data.completed_count} sessions completed · ${Math.round(data.completion_rate * 100)}% completion`} />

      {data.completed_count === 0 ? (
        <EmptyState icon={Users} title="No completed assessments in this group yet"
          description="Once members complete assessments, you'll see team-level capability data here." />
      ) : (
        <>
          {/* Distribution chart */}
          <Card className="mb-6">
            <CardHeader title="Capability distribution" subtitle="Average score per competency across the team" />
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={Object.values(dist).slice(0, 14)} margin={{ top: 10, right: 10, bottom: 60, left: 10 }}>
                  <CartesianGrid stroke="#E2E8F0" vertical={false} />
                  <XAxis dataKey="competency" tick={{ fontSize: 10, fill: '#475569' }} angle={-30} textAnchor="end" interval={0} height={60} />
                  <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} />
                  <Tooltip cursor={{ fill: '#F1F5F9' }} />
                  <Bar dataKey="mean" fill="#7C3AED" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>

          {/* Member matrix — simplified table view */}
          <Card>
            <CardHeader title="Capability matrix"
              subtitle={data.individual_scores_visible ? 'Individual scores per member' : 'Anonymised — group admins see named scores'} />
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-xs uppercase tracking-wider text-ink-500 border-b border-ink-200">
                  <tr>
                    <th className="text-left py-3 px-2">Member</th>
                    <th className="text-right py-3 px-2">Sessions</th>
                    <th className="text-right py-3 px-2">Avg score</th>
                  </tr>
                </thead>
                <tbody>
                  {matrix.map((m, i) => {
                    const scores = Object.values(m.competencies).map(c => c.score);
                    const avg = scores.length ? Math.round(scores.reduce((a, c) => a + c, 0) / scores.length) : 0;
                    return (
                      <tr key={i} className="border-b border-ink-100 hover:bg-ink-50/40">
                        <td className="py-3 px-2">
                          {data.individual_scores_visible && m.user_id ? (
                            <Link to={`/insights/individuals/${m.user_id}`} className="font-medium text-ink-900 hover:text-primary-700">
                              {m.display_name}
                            </Link>
                          ) : (
                            <span className="font-medium text-ink-700">{m.display_name}</span>
                          )}
                          {m.job_title ? <div className="text-xs text-ink-500">{m.job_title}</div> : null}
                        </td>
                        <td className="text-right py-3 px-2 text-ink-600">{m.sessions_completed}</td>
                        <td className="text-right py-3 px-2">
                          {scores.length ? <ScoreBadge score={avg} /> : <Badge variant="grey">No data</Badge>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}
    </>
  );
}
